/**
 * Audit Forms Page - Survey Readiness & Regulatory Compliance
 *
 * FERP Domain-Agnostic: Uses "Audit" primitive
 * Healthcare: CMS survey forms, state survey tools
 * ERP: SOX audit checklists, internal audit forms
 * Legal: Compliance audit trails
 *
 * Routes:
 * - /compliance/audit-forms - List all audit forms
 * - /compliance/audit-forms/:formId - Form detail with checkpoints
 * - /compliance/survey-readiness - Dashboard view
 */

import React, { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  Table,
  TextInput,
  Select,
  Tabs,
  Alert,
  Loader,
  Progress,
  ThemeIcon,
  Tooltip,
  ActionIcon,
  Paper,
} from '@mantine/core';
import {
  IconClipboardCheck,
  IconAlertTriangle,
  IconShieldCheck,
  IconSearch,
  IconFilter,
  IconChartBar,
  IconFileDescription,
  IconExternalLink,
} from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAuditFormsList,
  useAuditForm,
  useAuditCheckpoints,
  useCriticalCheckpoints,
  useTopDeficiencies,
  useSurveyReadiness,
  type AuditForm,
  type AuditCheckpoint,
  type DeficiencyAnalysis,
} from '@ferp/workflow-forms';
import { PageHeader } from '@ferp/workforce';

// ============================================================
// Audit Forms List View
// ============================================================

function AuditFormsList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [programFilter, setProgramFilter] = useState<string | null>(null);

  const { forms, loading, error } = useAuditFormsList({
    program: programFilter || undefined,
  });

  const filteredForms = forms.filter(
    (f) =>
      f.name.toLowerCase().includes(search.toLowerCase()) ||
      f.formId.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <Stack align="center" py="xl">
        <Loader />
        <Text c="dimmed">Loading audit forms...</Text>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error Loading Forms">
        {error.message}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group>
        <TextInput
          placeholder="Search forms..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1 }}
        />
        <Select
          placeholder="Filter by program"
          leftSection={<IconFilter size={16} />}
          data={[
            { value: 'medicare', label: 'Medicare' },
            { value: 'medicaid', label: 'Medicaid' },
            { value: 'state', label: 'State Survey' },
            { value: 'life_safety', label: 'Life Safety Code' },
          ]}
          value={programFilter}
          onChange={setProgramFilter}
          clearable
          w={200}
        />
      </Group>

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Form ID</Table.Th>
            <Table.Th>Name</Table.Th>
            <Table.Th>Program</Table.Th>
            <Table.Th>Jurisdiction</Table.Th>
            <Table.Th>Checkpoints</Table.Th>
            <Table.Th>Critical</Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {filteredForms.map((form) => (
            <Table.Tr
              key={form.formId}
              onClick={() => navigate(`/compliance/audit-forms/${form.formId}`)}
              style={{ cursor: 'pointer' }}
            >
              <Table.Td>
                <Text fw={500}>{form.formId}</Text>
              </Table.Td>
              <Table.Td>{form.name}</Table.Td>
              <Table.Td>
                <Badge variant="light">{form.program || 'General'}</Badge>
              </Table.Td>
              <Table.Td>{form.jurisdiction || 'Federal'}</Table.Td>
              <Table.Td>{form.checkpointCount}</Table.Td>
              <Table.Td>
                {form.criticalCount > 0 && (
                  <Badge color="red" variant="filled">
                    {form.criticalCount}
                  </Badge>
                )}
              </Table.Td>
              <Table.Td>
                <ActionIcon variant="subtle" size="sm">
                  <IconExternalLink size={16} />
                </ActionIcon>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {filteredForms.length === 0 && (
        <Text c="dimmed" ta="center" py="xl">
          No audit forms found matching your criteria.
        </Text>
      )}
    </Stack>
  );
}

// ============================================================
// Audit Form Detail View
// ============================================================

function AuditFormDetail({ formId }: { formId: string }) {
  const { form, loading: formLoading, error: formError } = useAuditForm(formId);
  const {
    checkpoints,
    loading: checkpointsLoading,
    groupedBySection,
    criticalOnly,
    lifeSafetyOnly,
  } = useAuditCheckpoints({ formId });

  const loading = formLoading || checkpointsLoading;

  if (loading) {
    return (
      <Stack align="center" py="xl">
        <Loader />
        <Text c="dimmed">Loading form details...</Text>
      </Stack>
    );
  }

  if (formError || !form) {
    return (
      <Alert color="red" title="Error">
        {formError?.message || 'Form not found'}
      </Alert>
    );
  }

  const critical = criticalOnly();
  const lifeSafety = lifeSafetyOnly();
  const sections = groupedBySection();

  return (
    <Stack gap="lg">
      {/* Form Header */}
      <Card withBorder>
        <Group justify="space-between" mb="md">
          <div>
            <Title order={3}>{form.name}</Title>
            <Text c="dimmed">{form.formId}</Text>
          </div>
          <Group>
            <Badge size="lg" variant="light">
              {form.program}
            </Badge>
            <Badge size="lg" variant="outline">
              {form.jurisdiction}
            </Badge>
          </Group>
        </Group>

        {form.description && <Text mb="md">{form.description}</Text>}

        <Group>
          <Paper p="xs" withBorder>
            <Text size="sm" c="dimmed">
              Total Checkpoints
            </Text>
            <Text size="xl" fw={700}>
              {form.checkpointCount}
            </Text>
          </Paper>
          <Paper p="xs" withBorder>
            <Text size="sm" c="dimmed">
              Critical Items
            </Text>
            <Text size="xl" fw={700} c="red">
              {critical.length}
            </Text>
          </Paper>
          <Paper p="xs" withBorder>
            <Text size="sm" c="dimmed">
              Life Safety
            </Text>
            <Text size="xl" fw={700} c="orange">
              {lifeSafety.length}
            </Text>
          </Paper>
        </Group>
      </Card>

      {/* Life Safety Alert */}
      {lifeSafety.length > 0 && (
        <Alert
          color="orange"
          title="Life Safety Items"
          icon={<IconAlertTriangle />}
        >
          This form contains {lifeSafety.length} life safety checkpoints that
          require immediate attention during surveys.
        </Alert>
      )}

      {/* Checkpoints by Section */}
      <Card withBorder>
        <Title order={4} mb="md">
          Checkpoints
        </Title>

        {Object.entries(sections).map(([sectionId, sectionCheckpoints]) => (
          <div key={sectionId}>
            <Text fw={600} mb="xs" mt="md">
              {sectionId === 'unsectioned' ? 'General' : sectionId}
            </Text>
            <Table>
              <Table.Tbody>
                {sectionCheckpoints.map((cp) => (
                  <Table.Tr key={cp.checkpointId}>
                    <Table.Td w={100}>
                      <Badge
                        color={
                          cp.isLifeSafety
                            ? 'orange'
                            : cp.isCritical
                              ? 'red'
                              : 'blue'
                        }
                      >
                        {cp.tagNumber}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{cp.description}</Text>
                      {cp.regulatoryCitation && (
                        <Text size="xs" c="dimmed">
                          {cp.regulatoryCitation}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td w={120}>
                      <Group gap="xs">
                        {cp.isCritical && (
                          <Tooltip label="Critical">
                            <ThemeIcon size="sm" color="red" variant="light">
                              <IconAlertTriangle size={12} />
                            </ThemeIcon>
                          </Tooltip>
                        )}
                        {cp.isLifeSafety && (
                          <Tooltip label="Life Safety">
                            <ThemeIcon size="sm" color="orange" variant="light">
                              <IconShieldCheck size={12} />
                            </ThemeIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        ))}
      </Card>
    </Stack>
  );
}

// ============================================================
// Survey Readiness Dashboard
// ============================================================

function SurveyReadinessDashboard() {
  const {
    forms,
    criticalCheckpoints,
    lifeSafetyCount,
    topDeficiencies,
    highSeverityDeficiencies,
    loading,
    error,
  } = useSurveyReadiness();

  if (loading) {
    return (
      <Stack align="center" py="xl">
        <Loader />
        <Text c="dimmed">Loading survey readiness data...</Text>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error">
        {error.message}
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      {/* Summary Cards */}
      <Group grow>
        <Card withBorder>
          <Group>
            <ThemeIcon size="xl" variant="light" color="blue">
              <IconFileDescription size={24} />
            </ThemeIcon>
            <div>
              <Text size="xl" fw={700}>
                {forms.length}
              </Text>
              <Text size="sm" c="dimmed">
                Active Forms
              </Text>
            </div>
          </Group>
        </Card>

        <Card withBorder>
          <Group>
            <ThemeIcon size="xl" variant="light" color="red">
              <IconAlertTriangle size={24} />
            </ThemeIcon>
            <div>
              <Text size="xl" fw={700}>
                {criticalCheckpoints.length}
              </Text>
              <Text size="sm" c="dimmed">
                Critical Checkpoints
              </Text>
            </div>
          </Group>
        </Card>

        <Card withBorder>
          <Group>
            <ThemeIcon size="xl" variant="light" color="orange">
              <IconShieldCheck size={24} />
            </ThemeIcon>
            <div>
              <Text size="xl" fw={700}>
                {lifeSafetyCount}
              </Text>
              <Text size="sm" c="dimmed">
                Life Safety Items
              </Text>
            </div>
          </Group>
        </Card>

        <Card withBorder>
          <Group>
            <ThemeIcon size="xl" variant="light" color="yellow">
              <IconChartBar size={24} />
            </ThemeIcon>
            <div>
              <Text size="xl" fw={700}>
                {highSeverityDeficiencies.length}
              </Text>
              <Text size="sm" c="dimmed">
                High-Risk Tags
              </Text>
            </div>
          </Group>
        </Card>
      </Group>

      {/* Top Deficiencies */}
      <Card withBorder>
        <Title order={4} mb="md">
          Top Deficiencies to Prepare For
        </Title>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Tag</Table.Th>
              <Table.Th>Description</Table.Th>
              <Table.Th>Frequency</Table.Th>
              <Table.Th>Severity</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {topDeficiencies.map((d) => (
              <Table.Tr key={d.tagNumber}>
                <Table.Td>
                  <Badge>{d.tagNumber}</Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{d.description}</Text>
                </Table.Td>
                <Table.Td>
                  <Progress value={d.frequency} size="sm" w={100} />
                </Table.Td>
                <Table.Td>
                  <Badge
                    color={
                      d.averageSeverity >= 3
                        ? 'red'
                        : d.averageSeverity >= 2
                          ? 'yellow'
                          : 'green'
                    }
                  >
                    {d.averageSeverity.toFixed(1)}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>

      {/* Critical Checkpoints */}
      <Card withBorder>
        <Title order={4} mb="md">
          Critical Checkpoints to Review
        </Title>
        <Stack gap="xs">
          {criticalCheckpoints.slice(0, 10).map((cp) => (
            <Paper key={cp.checkpointId} p="sm" withBorder>
              <Group justify="space-between">
                <Group>
                  <Badge color={cp.isLifeSafety ? 'orange' : 'red'}>
                    {cp.tagNumber}
                  </Badge>
                  <Text size="sm">{cp.description}</Text>
                </Group>
                {cp.correctionTimeframe && (
                  <Text size="xs" c="dimmed">
                    Correction: {cp.correctionTimeframe}
                  </Text>
                )}
              </Group>
            </Paper>
          ))}
        </Stack>
      </Card>
    </Stack>
  );
}

// ============================================================
// Main Page Component
// ============================================================

export function AuditFormsPage() {
  const { formId } = useParams<{ formId?: string }>();

  return (
    <Container size="xl">
      <PageHeader
        title="Audit Forms"
        description="Regulatory survey forms and compliance checkpoints"
        icon={<IconClipboardCheck size={24} />}
        breadcrumbs={[
          { label: 'Compliance', href: '/compliance' },
          { label: 'Audit Forms', href: '/compliance/audit-forms' },
          ...(formId ? [{ label: formId }] : []),
        ]}
      />

      {formId ? (
        <AuditFormDetail formId={formId} />
      ) : (
        <Tabs defaultValue="forms">
          <Tabs.List mb="md">
            <Tabs.Tab value="forms" leftSection={<IconFileDescription size={16} />}>
              All Forms
            </Tabs.Tab>
            <Tabs.Tab value="readiness" leftSection={<IconShieldCheck size={16} />}>
              Survey Readiness
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="forms">
            <AuditFormsList />
          </Tabs.Panel>

          <Tabs.Panel value="readiness">
            <SurveyReadinessDashboard />
          </Tabs.Panel>
        </Tabs>
      )}
    </Container>
  );
}

export default AuditFormsPage;
